import React, { useEffect, useState } from 'react';
import { 
  Table, 
  Spinner, 
  Container, 
  Card, 
  Badge, 
  Button, 
  Form,
  Modal,
  Row,
  Col,
  InputGroup,
  Pagination
} from 'react-bootstrap';
import axios from 'axios';
import { 
  FaFilePdf, 
  FaPlayCircle, 
  FaSearch, 
  FaFilter,
  FaDownload,
  FaEye,
  FaUserCircle,
  FaEnvelope,
  FaPhoneAlt,
  FaGraduationCap,
  FaBriefcase,
  FaHistory,
  FaSortAmountDown,
  FaSortAmountUp,
  FaDollarSign,
  FaUserCheck,
  FaCalendarAlt
} from 'react-icons/fa';
import moment from 'moment';

const SubmissionsList = () => {
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ program: '', cohort: '', status: '' });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    axios.get('https://api.collegeall.in/api/v1/get-submissions')
      .then(res => {
        if (res.data.success) {
          setSubmissions(res.data.submissions || []);
          setFilteredSubmissions(res.data.submissions || []);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching submissions:', err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    let result = [...submissions];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(sub => 
        sub.name?.toLowerCase().includes(term) || 
        sub.email?.toLowerCase().includes(term) ||
        sub.program?.toLowerCase().includes(term)
      );
    }
    if (filters.program) result = result.filter(sub => sub.program === filters.program);
    if (filters.cohort) result = result.filter(sub => sub.cohort === filters.cohort);
    if (filters.status) result = result.filter(sub => sub.status === filters.status);
    
    setFilteredSubmissions(result);
    setCurrentPage(1);
  }, [searchTerm, filters, submissions]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') direction = 'descending';
    setSortConfig({ key, direction });
    const sortedData = [...filteredSubmissions].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'ascending' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'ascending' ? 1 : -1;
      return 0;
    });
    setFilteredSubmissions(sortedData);
  };

  const getUniqueValues = (key) => [...new Set(submissions.map(item => item[key]).filter(Boolean))];

  const getStatusBadge = (status) => {
    const statusMap = { 'New': 'primary', 'Reviewed': 'info', 'Shortlisted': 'success', 'Rejected': 'danger', 'Hired': 'success' };
    const label = status || 'New';
    return <Badge bg={`${statusMap[label] || 'secondary'}-subtle`} text={statusMap[label] || 'secondary'} className={`border border-${statusMap[label] || 'secondary'}-subtle px-2 fw-bold`}>{label}</Badge>;
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredSubmissions.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredSubmissions.length / itemsPerPage);

  const renderPaginationItems = () => {
    const items = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        items.push(<Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>{i}</Pagination.Item>);
      }
      return items;
    }

    let start = currentPage - 1;
    let end = currentPage + 1;

    if (start <= 1) {
      start = 1;
      end = 3;
    } else if (end >= totalPages) {
      end = totalPages;
      start = totalPages - 2;
    }

    if (start > 1) {
      items.push(<Pagination.Ellipsis key="start-el" disabled />);
    }

    for (let i = start; i <= end; i++) {
      if (i < totalPages) {
        items.push(
          <Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>
            {i}
          </Pagination.Item>
        );
      }
    }

    if (end < totalPages - 1) {
      items.push(<Pagination.Ellipsis key="end-el" disabled />);
    }

    items.push(
      <Pagination.Item key={totalPages} active={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>
        {totalPages}
      </Pagination.Item>
    );

    return items;
  };

  return (
    <div className="submissions-mgmt-container p-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">Placement Records</h2>
          <p className="text-muted small mb-0">Unified dashboard for candidate submissions and recruitment outcomes</p>
        </div>
        <Button variant="primary" className="btn-modern shadow-sm">
          <FaDownload className="me-2" /> Export Global Sheet
        </Button>
      </div>

      {/* Summary Row */}
      <Row className="g-3 mb-4">
        {[
          { title: 'Total Submissions', count: submissions.length, icon: <FaHistory />, color: '#6366f1' },
          { title: 'Shortlisted', count: submissions.filter(s => s.status === 'Shortlisted').length, icon: <FaUserCheck />, color: '#10b981' },
          { title: 'Avg Expected CTC', count: '₹ 12.4L', icon: <FaDollarSign />, color: '#f59e0b' },
          { title: 'New Today', count: 3, icon: <FaCalendarAlt />, color: '#ec4899' },
        ].map((stat, i) => (
          <Col md={3} key={i}>
            <Card className="border-0 shadow-sm metric-card p-3">
              <div className="d-flex align-items-center">
                <div className="metric-icon" style={{ color: stat.color, background: `${stat.color}15` }}>{stat.icon}</div>
                <div className="ms-3">
                  <div className="small text-muted fw-bold text-uppercase" style={{ fontSize: '0.65rem' }}>{stat.title}</div>
                  <h4 className="fw-bold mb-0">{stat.count}</h4>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
        <div className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div className="d-flex gap-3 align-items-center">
            <Form.Select size="sm" className="border-0 bg-light fw-bold" style={{ width: '150px' }}
              value={filters.program} onChange={e => setFilters({...filters, program: e.target.value})}>
              <option value="">All Programs</option>
              {getUniqueValues('program').map((p, i) => <option key={i} value={p}>{p}</option>)}
            </Form.Select>
            <Form.Select size="sm" className="border-0 bg-light fw-bold" style={{ width: '130px' }}
              value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})}>
              <option value="">All Statuses</option>
              {['New', 'Reviewed', 'Shortlisted', 'Hired', 'Rejected'].map((s, i) => <option key={i} value={s}>{s}</option>)}
            </Form.Select>
          </div>
          <InputGroup className="search-group" style={{ maxWidth: '280px' }}>
            <InputGroup.Text className="bg-light border-0"><FaSearch className="text-muted small" /></InputGroup.Text>
            <Form.Control className="bg-light border-0 small shadow-none" placeholder="Search records..."
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          </InputGroup>
        </div>

        <div className="table-responsive">
          <Table hover className="modern-table mb-0 align-middle">
            <thead>
              <tr>
                <th className="ps-4 cursor-pointer" onClick={() => requestSort('name')}>
                  Applicant {sortConfig.key === 'name' ? (sortConfig.direction === 'ascending' ? <FaSortAmountDown className="ms-1"/> : <FaSortAmountUp className="ms-1"/>) : null}
                </th>
                <th>Program/Cohort</th>
                <th>Experience</th>
                <th>CTC (Current/Exp)</th>
                <th>Status</th>
                <th className="text-center">Assets</th>
                <th className="text-end pe-4">Command</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="text-center py-5"><Spinner animation="border" size="sm" variant="primary" /></td></tr>
              ) : currentItems.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-5 text-muted small">No placement records found.</td></tr>
              ) : (
                currentItems.map((s, i) => (
                  <tr key={i} className="submission-row">
                    <td className="ps-4">
                      <div className="d-flex align-items-center">
                        <div className="avatar-mini bg-primary text-white">{(s.name || 'U').charAt(0)}</div>
                        <div className="ms-3">
                          <div className="fw-bold text-dark mb-0">{s.name}</div>
                          <div className="x-small text-muted">{s.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="small fw-bold text-dark">{s.program}</div>
                      <div className="x-small text-muted">{s.cohort}</div>
                    </td>
                    <td>
                      <div className="small text-muted d-flex align-items-center">
                        <FaBriefcase className="me-1 text-info" /> {s.total_work_experience || 'Fresher'}
                      </div>
                    </td>
                    <td>
                      <div className="small fw-bold text-success">₹ {s.current_ctc || 0}L</div>
                      <div className="x-small text-primary">₹ {s.expected_ctc || 0}L</div>
                    </td>
                    <td>{getStatusBadge(s.status)}</td>
                    <td className="text-center">
                      <div className="d-flex justify-content-center gap-2">
                        {s.cv && <a href={`https://api.collegeall.in/api/v1/uploads/${s.cv}`} target="_blank" className="asset-link pdf"><FaFilePdf /></a>}
                        {s.video && <a href={`https://api.collegeall.in/api/v1/uploads/${s.video}`} target="_blank" className="asset-link video"><FaPlayCircle /></a>}
                      </div>
                    </td>
                    <td className="text-end pe-4">
                      <Button variant="light" size="sm" className="btn-icon text-primary shadow-none" onClick={() => { setSelectedSubmission(s); setShowModal(true); }}><FaEye /></Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        {totalPages > 1 && (
          <div className="p-3 bg-light border-top d-flex justify-content-between align-items-center">
            <div className="small text-muted">Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredSubmissions.length)} of {filteredSubmissions.length}</div>
            <Pagination className="mb-0 modern-pagination">
              <Pagination.Prev onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} />
              {renderPaginationItems()}
              <Pagination.Next onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} />
            </Pagination>
          </div>
        )}
      </Card>

      {/* Submission Details Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered className="submission-detail-modal">
        <Modal.Body className="p-4">
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div className="d-flex align-items-center">
              <div className="avatar-large bg-indigo-subtle text-indigo">{(selectedSubmission?.name || 'U').charAt(0)}</div>
              <div className="ms-3">
                <h4 className="fw-bold mb-1">{selectedSubmission?.name}</h4>
                <div className="d-flex gap-3 text-muted x-small fw-bold">
                  <span><FaEnvelope className="me-1" /> {selectedSubmission?.email}</span>
                  <span><FaPhoneAlt className="me-1" /> {selectedSubmission?.phone || 'N/A'}</span>
                </div>
              </div>
            </div>
            {getStatusBadge(selectedSubmission?.status)}
          </div>

          <Row className="g-4 mb-4">
            <Col md={6}>
              <div className="section-label mb-3">Academic & Program Path</div>
              <div className="info-box p-3 border rounded-3 bg-white">
                <div className="mb-3 d-flex align-items-center">
                  <div className="icon-circle-mini bg-light me-3"><FaGraduationCap /></div>
                  <div>
                    <div className="x-small text-muted fw-bold">Program</div>
                    <div className="small fw-bold">{selectedSubmission?.program}</div>
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  <div className="icon-circle-mini bg-light me-3"><FaHistory /></div>
                  <div>
                    <div className="x-small text-muted fw-bold">Cohort Group</div>
                    <div className="small fw-bold">{selectedSubmission?.cohort}</div>
                  </div>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="section-label mb-3">Compensations (Annual)</div>
              <div className="info-box p-3 border rounded-3 bg-white">
                <div className="mb-3 d-flex align-items-center">
                  <div className="icon-circle-mini bg-success-subtle text-success me-3"><FaDollarSign /></div>
                  <div>
                    <div className="x-small text-muted fw-bold">Current CTC</div>
                    <div className="small fw-bold">₹ {selectedSubmission?.current_ctc || 0} Lakhs</div>
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  <div className="icon-circle-mini bg-primary-subtle text-primary me-3"><FaDollarSign /></div>
                  <div>
                    <div className="x-small text-muted fw-bold">Expected CTC</div>
                    <div className="small fw-bold">₹ {selectedSubmission?.expected_ctc || 0} Lakhs</div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          <div className="section-label mb-3">Additional Intelligence</div>
          <div className="bg-light p-3 rounded-3 small text-muted mb-4">
            {selectedSubmission?.additional_info || 'No additional details were provided with this submission.'}
          </div>

          <div className="d-grid gap-2">
             <Button variant="primary" className="fw-bold py-2 shadow-sm">INITIATE REVIEW</Button>
             <Button variant="light" onClick={() => setShowModal(false)} className="fw-bold text-muted border-0">DISMISS</Button>
          </div>
        </Modal.Body>
      </Modal>

      <style>{`
        .submissions-mgmt-container {
          background: #f8fafc;
          min-height: 100vh;
          font-family: 'Inter', sans-serif;
        }

        .metric-card { border-radius: 12px; }
        .metric-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.2rem; }

        .modern-table thead th {
          background: #f8fafc;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #94a3b8;
          border-top: 0;
          font-weight: 700;
          padding: 1rem !important;
        }

        .submission-row:hover { background-color: #f1f5f9; }

        .avatar-mini { width: 34px; height: 34px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.8rem; }
        .avatar-large { width: 60px; height: 60px; border-radius: 16px; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 1.5rem; }

        .asset-link {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          font-size: 0.9rem;
          transition: all 0.2s;
        }
        .asset-link.pdf { background: #fee2e2; color: #ef4444; }
        .asset-link.video { background: #e0f2fe; color: #0ea5e9; }
        .asset-link:hover { transform: scale(1.1); }

        .btn-icon { width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; padding: 0; border-radius: 8px; }

        .section-label { font-size: 0.7rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; color: #94a3b8; }
        .icon-circle-mini { width: 36px; height: 36px; border-radius: 8px; display: flex; align-items: center; justify-content: center; }

        .submission-detail-modal .modal-content { border-radius: 1.5rem; border: 0; overflow: hidden; }
        .bg-indigo-subtle { background: #e0e7ff; }
        .text-indigo { color: #4f46e5; }
        .x-small { font-size: 0.75rem; }
        .modern-pagination .page-link { border: 0; color: #64748b; font-weight: 600; border-radius: 6px; }
        .modern-pagination .page-item.active .page-link { background: #6366f1; color: white; }
        .btn-modern { font-weight: 600; font-size: 0.85rem; border-radius: 8px; padding: 0.6rem 1.25rem; }
      `}</style>
    </div>
  );
};

export default SubmissionsList;
